import React, { useEffect, useState, useCallback } from 'react';
import {
  getRoles, createRole, updateRole, deleteRole,
  getRolePermissions, assignRolePermissions, getPermissions,
} from '../../api/client';
import { Shield, Plus, Edit2, Trash2, Key, X, Check, ChevronDown, ChevronRight } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────
const MODULE_LABELS = {
  dashboard: 'Dashboard',
  work_orders: 'Work Orders',
  technicians: 'Technicians',
  parts: 'Parts Inventory',
  invoices: 'Invoices',
  ai_assistant: 'AI Assistant',
  billing: 'Billing',
  system: 'System',
};
const MODULE_ORDER = Object.keys(MODULE_LABELS);

const TYPE_BADGE = {
  page:   { bg: '#DBEAFE', color: '#1D4ED8', label: 'Page' },
  button: { bg: '#D1FAE5', color: '#065F46', label: 'Button' },
  api:    { bg: '#EDE9FE', color: '#5B21B6', label: 'API' },
  module: { bg: '#FEF3C7', color: '#92400E', label: 'Module' },
};

function Badge({ type }) {
  const cfg = TYPE_BADGE[type] || TYPE_BADGE.page;
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
      {cfg.label}
    </span>
  );
}

// ── Modal ─────────────────────────────────────────────────────────
function RoleModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    display_name: initial?.display_name || '',
    description: initial?.description || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isEdit = !!initial;

  const handleSave = async () => {
    if (!form.display_name.trim()) { setErr('Display name is required'); return; }
    if (!isEdit && !form.name.trim()) { setErr('Role name is required'); return; }
    setSaving(true);
    setErr('');
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
            {isEdit ? 'Edit Role' : 'New Role'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
            <X size={18} />
          </button>
        </div>

        {err && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
            {err}
          </div>
        )}

        {[
          { label: 'Role Name (slug)', key: 'name', disabled: isEdit, placeholder: 'e.g. senior_technician' },
          { label: 'Display Name', key: 'display_name', placeholder: 'e.g. Senior Technician' },
          { label: 'Description', key: 'description', placeholder: 'Optional description' },
        ].map(({ label, key, disabled, placeholder }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
            <input
              value={form[key]}
              disabled={disabled}
              onChange={e => setForm({ ...form, [key]: e.target.value })}
              placeholder={placeholder}
              style={{
                width: '100%', padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8,
                fontSize: 13, background: disabled ? '#F9FAFB' : '#fff', color: disabled ? '#9CA3AF' : '#111827',
              }}
            />
            {key === 'name' && !isEdit && (
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>Lowercase letters, numbers, underscores only. Used as the role identifier.</span>
            )}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '9px 18px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save Role'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Assign Permissions Drawer ─────────────────────────────────────
function PermissionsDrawer({ role, onClose, onSaved }) {
  const [allPerms, setAllPerms] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getPermissions(), getRolePermissions(role.id)])
      .then(([allRes, roleRes]) => {
        setAllPerms(allRes.data);
        const currentIds = new Set(roleRes.data.permissions.map(p => p.id));
        setSelected(currentIds);
        // Expand all modules by default
        const mods = {};
        allRes.data.forEach(p => { mods[p.module] = true; });
        setExpanded(mods);
      })
      .finally(() => setLoading(false));
  }, [role.id]);

  const byModule = MODULE_ORDER.reduce((acc, mod) => {
    const perms = allPerms.filter(p => p.module === mod);
    if (perms.length) acc[mod] = perms;
    return acc;
  }, {});

  const togglePerm = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleModule = (mod) => {
    const modPerms = byModule[mod] || [];
    const allSelected = modPerms.every(p => selected.has(p.id));
    setSelected(prev => {
      const next = new Set(prev);
      modPerms.forEach(p => allSelected ? next.delete(p.id) : next.add(p.id));
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await assignRolePermissions(role.id, { permission_ids: [...selected] });
      onSaved();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex' }}>
      {/* Overlay */}
      <div style={{ flex: 1, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      {/* Drawer */}
      <div style={{ width: 480, background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>Assign Permissions</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6B7280' }}>{role.display_name} · {selected.size} selected</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {loading ? (
            <p style={{ textAlign: 'center', color: '#6B7280', padding: 40 }}>Loading permissions...</p>
          ) : (
            Object.entries(byModule).map(([mod, perms]) => {
              const allSel = perms.every(p => selected.has(p.id));
              const someSel = perms.some(p => selected.has(p.id));
              const isOpen = expanded[mod];

              return (
                <div key={mod} style={{ marginBottom: 8, border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                  {/* Module header */}
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#F9FAFB', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setExpanded(e => ({ ...e, [mod]: !e[mod] }))}
                  >
                    <button
                      style={{
                        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                        border: `2px solid ${allSel ? '#2563EB' : someSel ? '#93C5FD' : '#D1D5DB'}`,
                        background: allSel ? '#2563EB' : someSel ? '#DBEAFE' : '#fff',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                      onClick={e => { e.stopPropagation(); toggleModule(mod); }}
                    >
                      {allSel && <Check size={10} color="#fff" />}
                    </button>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#374151', flex: 1 }}>
                      {MODULE_LABELS[mod] || mod}
                    </span>
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>{perms.filter(p => selected.has(p.id)).length}/{perms.length}</span>
                    {isOpen ? <ChevronDown size={14} color="#9CA3AF" /> : <ChevronRight size={14} color="#9CA3AF" />}
                  </div>

                  {/* Permission rows */}
                  {isOpen && (
                    <div>
                      {perms.map(perm => (
                        <label
                          key={perm.id}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderTop: '1px solid #F3F4F6' }}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(perm.id)}
                            onChange={() => togglePerm(perm.id)}
                            style={{ width: 14, height: 14, accentColor: '#2563EB', flexShrink: 0 }}
                          />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{perm.name}</div>
                            <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{perm.code}</div>
                          </div>
                          <Badge type={perm.type} />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: '10px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────
export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | { mode: 'create' | 'edit', role? }
  const [drawer, setDrawer] = useState(null); // null | role object
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getRoles().then(r => setRoles(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (modal.mode === 'create') {
      await createRole(form);
    } else {
      await updateRole(modal.role.id, form);
    }
    load();
  };

  const handleDelete = async (role) => {
    await deleteRole(role.id);
    setDeleteConfirm(null);
    load();
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Shield size={22} color="#2563EB" />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Role Management</h1>
        </div>
        <button
          onClick={() => setModal({ mode: 'create' })}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
        >
          <Plus size={15} /> New Role
        </button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
              {['Name', 'Display Name', 'Description', 'Type', 'Permissions', 'Actions'].map(h => (
                <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading...</td></tr>
            ) : roles.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No roles yet</td></tr>
            ) : (
              roles.map((role, i) => (
                <tr key={role.id} style={{ borderBottom: i < roles.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 13, color: '#374151', fontWeight: 600 }}>{role.name}</td>
                  <td style={{ padding: '12px 16px', fontSize: 14, color: '#111827', fontWeight: 500 }}>{role.display_name}</td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280', maxWidth: 220 }}>{role.description || '—'}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: role.is_system ? '#EFF6FF' : '#F0FDF4',
                      color: role.is_system ? '#1D4ED8' : '#166534',
                      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    }}>
                      {role.is_system ? 'System' : 'Custom'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: '#F3F4F6', color: '#374151', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      {role.permission_count}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setDrawer(role)}
                        title="Assign Permissions"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        <Key size={12} /> Permissions
                      </button>
                      <button
                        onClick={() => setModal({ mode: 'edit', role })}
                        title="Edit"
                        style={{ padding: '5px 8px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', color: '#374151' }}
                      >
                        <Edit2 size={13} />
                      </button>
                      {!role.is_system && (
                        <button
                          onClick={() => setDeleteConfirm(role)}
                          title="Delete"
                          style={{ padding: '5px 8px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, cursor: 'pointer', color: '#DC2626' }}
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit modal */}
      {modal && (
        <RoleModal
          initial={modal.mode === 'edit' ? modal.role : null}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {/* Permission assignment drawer */}
      {drawer && (
        <PermissionsDrawer
          role={drawer}
          onClose={() => setDrawer(null)}
          onSaved={load}
        />
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Delete Role?</h3>
            <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 20 }}>
              Delete <strong>{deleteConfirm.display_name}</strong>? This will remove all permission assignments for this role.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '9px 18px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} style={{ padding: '9px 18px', background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
